import React, { useEffect, useState, useRef } from 'react';
import PrimaryButton from '../common/PrimaryButton';
import ReusableTextField from '../common/ReusableTextField';
import EventService from "../../services/EventService.js";
import { useEventStore } from "../../store/eventStore";
import '../../assets/css/AddDetailsTab.css';

const FinalTouchUpTab = ({ onNext, onBack, registerValidator }) => {
  const campaignData = useEventStore((state) => state.eventDetails);
  const updateEventDetails = useEventStore((state) => state.updateEventDetails);
  const markSaved = useEventStore((state) => state.markSaved);

  // ✅ Destructure shared data
  const {
    campaignId,
    campaignName,
    startDate,
    endDate,
    eventTime,
    description,
    formFields,
    eventPriceTagLine,
    eventPrice,
    templateId,
    eventTemplateId,
    templateCategory,
    templateImage,
    templateHtml,
    templateSummary,
    videoConferenceLink,
    isDraft
  } = campaignData;


  const [hasSavedRazorpay, setHasSavedRazorpay] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    razorpayAccountName: "",
    razorpayKeyId: "",
    razorpayKeySecret: "",
    razorpayWebhookSecret: "",
    businessName: "",
    businessAddress: "",
    gstin: "",
    cin: "",
    businessEmail: "",
    businessPhone: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [profileExists, setProfileExists] = useState(false);

  // Utility to convert 24-hour time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "10:30 AM"; // default
    const [hoursStr, minutes] = time24.split(":");
    let hours = parseInt(hoursStr, 10);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
  fetchedRef.current = true;
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const resp = await EventService.getUserPaymentProfile();
      if (resp) {
        const d = resp;

        setProfileExists(true);

        setForm({
          razorpayAccountName: d.razorpayAccountName || "",
          razorpayKeyId: d.razorpayKeyId || "",
          razorpayKeySecret: d.razorpayKeySecret || "",
          razorpayWebhookSecret: d.razorpayWebhookPathSegment || "",
          businessName: d.businessName || "",
          businessAddress: d.businessAddress || "",
          gstin: d.gstin || "",
          cin: d.cin || "",
          businessEmail: d.businessEmail || "",
          businessPhone: d.businessPhone || "",
        });

        if (d.logoUrl) setLogoPreview(d.logoUrl);
      }
    } catch (err) {
      console.log("No existing Razorpay profile");
    }
  };

  const updateField = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      alert("Only PNG/JPG/WEBP allowed");
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleIntegrate = async (e) => {
    e.preventDefault();

     if (!validatePaymentFields()) return;

    const fd = new FormData();

    Object.keys(form).forEach(k => {
      fd.append(k.charAt(0).toUpperCase() + k.slice(1), form[k]);
    });

    if (logoFile) fd.append("LogoFile", logoFile);

    try {
      const resp = profileExists
        ? await EventService.updateUserPaymentProfile(fd)
        : await EventService.createUserPaymentProfile(fd);

      if (resp.success) {
        alert("Saved successfully!");
        setProfileExists(true);
        fetchProfile();
      }
    } catch (err) {
      alert("Failed to save");
    }
  };

  const validateFinalTouchUp = () => {debugger;
  if (!profileExists) {
    alert("Please integrate payment gateway first.");
    return false;
  }

  return true;
};


  const generateWebhookSecret = () => {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, "0")).join("");
  };

  // ✅ Update shared campaignData when conference link changes
  const handleConferenceChange = (e) => {
    updateEventDetails((prev) => ({
      videoConferenceLink: e.target.value,
    }));
  };

  // ✅ Save & Continue (move to Preview tab)
  const handleContinue = () => {
    if (!validateFinalTouchUp()) return;
    console.log('Final campaign data before continue:', campaignData);
    if (onNext) onNext(); // Navigate to Preview tab
  };

  const validatePaymentFields = () => {
  const missing = [];

  if (!form.razorpayAccountName?.trim())
    missing.push("Account Name");

  if (!form.razorpayKeyId?.trim())
    missing.push("Key ID");

  if (!form.razorpayKeySecret?.trim())
    missing.push("Key Secret");

  if (!form.razorpayWebhookSecret?.trim())
    missing.push("Webhook Secret");

  if (!form.businessName?.trim())
    missing.push("Business Name");

  if (!form.businessAddress?.trim())
    missing.push("Business Address");

  if (!form.businessEmail?.trim())
    missing.push("Business Email");

  if (!form.businessPhone?.trim())
    missing.push("Business Phone");

  if (!form.gstin?.trim())
    missing.push("GSTIN");

  if (!form.cin?.trim())
    missing.push("CIN");

  if (missing.length) {
    alert(
      `Please fill the following:\n\n${missing.join("\n")}`
    );
    return false;
  }

  return true;
};


  const handleSave = async () => {
    // Collect missing fields (excluding meetingLink)
    const missingFields = [];

    if (!campaignName) missingFields.push("Event Name");
    if (!description) missingFields.push("Event Description");
    if (!startDate) missingFields.push("Start Date");
    if (!endDate) missingFields.push("End Date");
    if (!eventTime) missingFields.push("Event Time");
    if (!eventPriceTagLine) missingFields.push("Price Tag Line");
    if (!eventPrice) missingFields.push("Event Price");
    if (!formFields?.length) missingFields.push("Form Fields");

    if (missingFields.length > 0) {
      alert(`Please fill in the following fields before saving:\n\n${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      eventId: campaignId,
      eventName: campaignName,
      eventDetails: description,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      isPreview: false,
      isDraft: isDraft,
      eventTime: formatTimeTo12Hour(eventTime) || "10:30 AM",
      eventPriceTagLine: eventPriceTagLine,
      eventEnterPrice: parseFloat(eventPrice) || 0,
      eventObjective: templateCategory,
      templateId: templateId,
      eventTemplateId: eventTemplateId,
      templateSummary: templateSummary,
      templateBody: templateHtml,
      templateImage: templateImage,
      eventInputs: formFields.map((field) => ({
        inputTitle: field.label,
        isRequire: field.required,
      })),
      meetingLink: videoConferenceLink, // optional
    };

    try {
      const response = await EventService.saveEvent(payload);

      if (response.data?.success) {
        const ev = response.data.data;
        updateEventDetails((prev) => ({
          campaignId: ev.id,
          templateId: ev.eventTemplate?.templateId || 0,
          eventTemplateId: ev.eventTemplate?.id || 0,
          templateHtml: ev.eventTemplate?.templateBody || "",
          templateSummary: ev.eventTemplate?.templateSummary || "",
          templateCategory: ev.eventTemplate?.templateSummary || "",
          templateImage: ev.eventTemplate?.templateImage,
          eventTemplateId: response.data.data.eventTemplate?.id || 0
        }), false);
        markSaved();
        alert("Event saved successfully!");
      } else {
        console.error("API Error:", response.data);
        alert("Failed to save event.");
      }
    } catch (error) {
      console.error("Request failed:", error);
      alert("An error occurred while saving the event.");
    }
  };
  
  useEffect(() => {
  if (registerValidator) {
    registerValidator(validateFinalTouchUp);
  }
}, [profileExists, validateFinalTouchUp]);


  return (
    <div className="p-4">
      {/* Video Conference */}
      <div className="mt-4">
        <ReusableTextField
          name="conferenceLink"
          label="Video Conference Link"
          placeholder="Enter the Video Conference link here and keep it ready. Once the user
          registers, the link will be sent to them via Email/WhatsApp."
          value={videoConferenceLink || ''}
          onChange={handleConferenceChange}
          fullWidth
        />
      </div>

      <br />

      {/* Integrate Email */}
      <div className="mb-4">
        <h6 className="fw-bold mb-2">Integrate Email</h6>
        <p className="mb-1">
          Looks like you have not integrated your email yet. Please integrate it
          now. <a href="/email">Click here</a>.
        </p>
      </div>

      <br />

      {/* Payment Gateway */}
      <div>
        <h6 className="fw-bold mb-2">Payment Gateway</h6>
        <p className="text-muted">
          Integrate your payment system to get the amount directly to your
          account when someone registers.
        </p>

        {profileExists && (
          <div className="mb-3">
            <p>Already have a Razorpay account?</p>

            <div className="form-check">
              <input type="radio" checked readOnly />
              <label>{form.razorpayAccountName}</label>
            </div>
          </div>
        )}


        <div className="border rounded p-3">
          <p className="small mb-3">
            We will need your API Key ID and API Key Secret (private key) to
            integrate your payment system. We will need this info only once.
          </p>
          <form onSubmit={handleIntegrate}>
            <ReusableTextField
              label="Account Name"
              value={form.razorpayAccountName}
              onChange={updateField("razorpayAccountName")}
            />

            <ReusableTextField
              label="Key ID"
              value={form.razorpayKeyId}
              onChange={updateField("razorpayKeyId")}
            />

            <ReusableTextField
              label="Key Secret"
              value={form.razorpayKeySecret}
              onChange={updateField("razorpayKeySecret")}
            />

            <div>

              {/* Header Row */}
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label mb-0 fw-semibold">
                  Webhook Secret
                </label>

                <div className="d-flex gap-2">

                  <button
                    type="button"
                    onClick={() =>
                      setForm(prev => ({
                        ...prev,
                        razorpayWebhookSecret: generateWebhookSecret()
                      }))
                    }
                    className="btn btn-sm btn-light border"
                  >
                    Regenerate
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(form.razorpayWebhookSecret);
                    }}
                    className="btn btn-sm btn-light border"
                  >
                    Copy
                  </button>

                </div>
              </div>

              {/* Input Field */}
              <ReusableTextField
                label=""  // hide internal label
                value={form.razorpayWebhookSecret}
                readOnly
                disabled
              />

            </div>


            <ReusableTextField
              label="Business Name"
              value={form.businessName}
              onChange={updateField("businessName")}
            />

            <ReusableTextField
              label="Business Address"
              value={form.businessAddress}
              onChange={updateField("businessAddress")}
            />


            <ReusableTextField
              label="Business Email"
              value={form.businessEmail}
              onChange={updateField("businessEmail")}
            />

            <ReusableTextField
              label="Business Phone"
              value={form.businessPhone}
              onChange={updateField("businessPhone")}
            />

            <ReusableTextField
              label="GSTIN"
              value={form.gstin}
              onChange={updateField("gstin")}
            />

            <ReusableTextField
              label="CIN"
              value={form.cin}
              onChange={updateField("cin")}
            />

            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
            />

            {logoPreview && (
              <img src={logoPreview} width="120" alt="logo" />
            )}


            <div className="d-flex justify-content-end integrate-btn">
              <PrimaryButton type="submit" disabled={saving}>
                {saving ? 'Integrating...' : 'Integrate'}
              </PrimaryButton>
            </div>

            <p className="small text-muted mt-3">
              By submitting, you are giving ProspectPulse (Nurturix) consent to
              store this info with ProspectPulse. ProspectPulse stores this info
              using secure encryption and will not share it externally. Please
              check our <a href="/privacy-policy">Data consent and privacy</a>{' '}
              policy.
            </p>
          </form>
        </div>
      </div>

      {/* ✅ Updated Bottom Buttons: Same layout as AddDetailsTab */}
      <div className="action-buttons">
        <button className="btn secondary go-back" onClick={onBack}>
          Back
        </button>
        <div className="right-actions">
          <button className="btn warn save" onClick={handleSave}>
            Save as Draft
          </button>
          <button
            className="btn success continue"
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalTouchUpTab;
